import { useRef, Children } from "react";
import { C, FONT } from "./theme";
import { useModalA11y } from "../useModalA11y";

// Shared Modal/Sheet shell for the Phase 1 design-system foundation (see
// linear-brewing-toast.md). This generalizes App.js's existing logout-confirm
// dialog — the one dialog in the app already built in the target Apple/iOS
// style (white card, centered text, hairline-divided text-button footer) —
// into a reusable component every hand-rolled `position:'fixed'` popup in
// the app can migrate onto, instead of each re-implementing its own
// backdrop/card/shadow/radius by hand.
//
// Purely a SHELL: backdrop, centered card, focus-trap + Escape (via the
// already-shared useModalA11y hook, the same one Workspace/Mail
// Merge/Sequences' own popups already use), click-outside-to-close. What
// goes inside is up to the caller — the ModalBody/ModalTitle/
// ModalDescription/ModalActions/ModalAction helpers below match the logout
// dialog's exact anatomy for simple confirm dialogs, but any content works
// (forms, lists, etc. — see how Settings' delete-account dialog uses it).
//
// Usage:
//   <Modal open={open} onClose={close} width={270}>
//     <ModalBody>
//       <ModalTitle>Log Out?</ModalTitle>
//       <ModalDescription>You'll be redirected to the login page.</ModalDescription>
//     </ModalBody>
//     <ModalActions>
//       <ModalAction onClick={close}>Cancel</ModalAction>
//       <ModalAction destructive onClick={confirm}>Log Out</ModalAction>
//     </ModalActions>
//   </Modal>
export function Modal({ open, onClose, width = 320, children, ariaLabel }) {
  const ref = useRef(null);
  useModalA11y(open, onClose, ref);
  if (!open) return null;
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, width, maxWidth: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.25)", fontFamily: FONT, outline: "none" }}
      >
        {children}
      </div>
    </div>
  );
}

// Padded content area — matches the logout dialog's "20px 18px 16px" body.
// center=true (default) matches the logout dialog's centered-text alert
// style; pass center={false} for form-style content (labels, inputs).
export function ModalBody({ children, center = true }) {
  return <div style={{ padding: "20px 18px 16px", textAlign: center ? "center" : "left" }}>{children}</div>;
}

export function ModalTitle({ children, color }) {
  return <p style={{ fontSize: 17, fontWeight: 600, color: color || C.text, marginBottom: 6 }}>{children}</p>;
}

export function ModalDescription({ children }) {
  return <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.4 }}>{children}</p>;
}

// Hairline-divided row of full-width text buttons, exactly the logout
// dialog's footer anatomy. Automatically adds the right-hand divider
// between buttons (not after the last one).
export function ModalActions({ children }) {
  const items = Children.toArray(children);
  return (
    <div style={{ display: "flex", borderTop: `1px solid ${C.border}` }}>
      {items.map((child, i) => (
        <div key={i} style={{ flex: 1, borderRight: i < items.length - 1 ? `1px solid ${C.border}` : "none" }}>
          {child}
        </div>
      ))}
    </div>
  );
}

export function ModalAction({ onClick, destructive, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ width: "100%", padding: "12px 0", background: "transparent", border: "none", fontSize: 16, color: destructive ? C.red : C.accent, fontWeight: destructive ? 600 : 400, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, fontFamily: FONT }}
    >
      {children}
    </button>
  );
}

export default Modal;
